import React from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// SortableChapterItem — draggable chapter row for reorder screen
function SortableChapterItem({ id, title, index, photoUrl }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '16px 18px',
        background: isDragging ? '#fbf8f1' : '#ffffff',
        border: isDragging ? '2px solid #7c2d12' : '1px solid rgba(15,23,42,0.15)',
        borderRadius: 12,
        boxShadow: isDragging ? '0 8px 32px rgba(0,0,0,0.15)' : 'none',
        cursor: 'grab',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
      }} {...attributes} {...listeners}>
        {/* Grip handle */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0, color: '#3c4653', fontSize: '1.1rem', lineHeight: 1, padding: '0 2px' }}>
          <span style={{ letterSpacing: 2 }}>⠿</span>
        </div>
        {/* Chapter number */}
        <div style={{
          width: 36, height: 36, flexShrink: 0,
          background: 'rgba(15,23,42,0.06)', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1rem', fontWeight: 600, color: '#3c4653', fontFamily: "'Lora', serif",
        }}>{index + 1}</div>
        {/* Title */}
        <div style={{ flex: 1, minWidth: 0, fontSize: '1.1rem', fontWeight: 500, color: '#0f172a', fontFamily: "'Lora', serif", overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {title}
        </div>
      </div>
    </div>
  );
}

// ReorderableChapterList — wrapper that uses hooks at top level (not inside conditionals)
export default function ReorderableChapterList({ pages, stories, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor)
  );
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onReorder}>
      <SortableContext items={pages.map(p => p.storyId)} strategy={verticalListSortingStrategy}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pages.map((page, idx) => {
            const story = stories.find(s => s.id === page.storyId);
            if (!story) return null;
            return (
              <SortableChapterItem
                key={page.storyId}
                id={page.storyId}
                title={story.title}
                index={idx}
                photoUrl={page.photoUrl}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}
